/**
 * Category picker (intake/spec §3.2, §6): a `role="radiogroup"` of full-width tappable radio-cards,
 * NOT a native <select>. Each card shows the routing department. Full keyboard support: arrow keys
 * move within the group (roving tabindex), Space/Enter select, Tab exits. SR-labelled + required.
 */
import { useId, useRef } from "react";
import type { IntakeCategory } from "../api/categories.js";
import { CheckIcon } from "./icons.js";

interface Props {
  categories: IntakeCategory[];
  value: string | null;
  onChange: (id: string) => void;
  /** Container id so the error summary can focus the group. */
  groupId: string;
  error?: string;
}

export function CategoryRadioGroup({
  categories,
  value,
  onChange,
  groupId,
  error,
}: Props): JSX.Element {
  const labelId = useId();
  const errorId = `${groupId}-error`;
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (from: number, dir: 1 | -1) => {
    const next = (from + dir + categories.length) % categories.length;
    const cat = categories[next];
    if (!cat) return;
    onChange(cat.id);
    refs.current[next]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight":
        e.preventDefault();
        move(index, 1);
        break;
      case "ArrowUp":
      case "ArrowLeft":
        e.preventDefault();
        move(index, -1);
        break;
      default:
        break;
    }
  };

  // Roving tabindex: the selected card is the single tab stop; if none selected, the first is.
  const activeIndex = Math.max(
    0,
    categories.findIndex((c) => c.id === value),
  );

  return (
    <div className="td-field">
      <span className="td-field__label" id={labelId}>
        What are you reporting?
        <span className="td-field__req" aria-hidden="true">
          {" "}
          *
        </span>
      </span>
      <div
        id={groupId}
        role="radiogroup"
        aria-labelledby={labelId}
        aria-required="true"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className="td-radio-cards"
      >
        {categories.map((cat, i) => {
          const selected = cat.id === value;
          return (
            <button
              key={cat.id}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={i === activeIndex ? 0 : -1}
              ref={(el) => {
                refs.current[i] = el;
              }}
              className={`td-radio-card${selected ? " is-selected" : ""}`}
              onClick={() => onChange(cat.id)}
              onKeyDown={(e) => onKeyDown(e, i)}
            >
              <span className="td-radio-card__mark" aria-hidden="true">
                {selected ? <CheckIcon width={16} height={16} /> : null}
              </span>
              <span className="td-radio-card__text">
                <span className="td-radio-card__label">{cat.label}</span>
                <span className="td-radio-card__dept">→ {cat.department}</span>
              </span>
            </button>
          );
        })}
      </div>
      {error ? (
        <p className="td-field__error" id={errorId}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
